import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { t, L } from "../lib/translations";
import { formatIsraelDate, formatIsraelDateTime } from "../lib/time";
import { Mail, MailOpen, CheckCheck, MessageSquare, Megaphone, Package, Gift, AlertTriangle, Bell, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_SHORTCUTS = [
  { key: "dashboard", path: "/dashboard",  icon: "🏠", label: { en: "Dashboard", ru: "Личный кабинет", he: "לוח בקרה" } },
  { key: "shop",      path: "/shop",        icon: "🛍️", label: { en: "Shop",      ru: "Магазин",       he: "חנות"     } },
  { key: "topup",     path: "/topup",       icon: "💳", label: { en: "Top Up",    ru: "Пополнение",    he: "טעינה"    } },
  { key: "tasks",     path: "/tasks",       icon: "🎯", label: { en: "Tasks",     ru: "Задания",       he: "משימות"   } },
  { key: "messages",  path: "/messages",    icon: "✉️", label: { en: "Messages",  ru: "Сообщения",     he: "הודעות"   } },
  { key: "support",   path: "/support",     icon: "💬", label: { en: "Support",   ru: "Поддержка",     he: "תמיכה"    } },
];

function renderMsgContent(text, navigate, lang) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(/(\{[\w]+\})/);
  return parts.map((part, i) => {
    const m = part.match(/^\{([\w]+)\}$/);
    if (m) {
      const s = NAV_SHORTCUTS.find(x => x.key === m[1]);
      if (s) return (
        <button key={i}
          onClick={(e) => { e.stopPropagation(); navigate(s.path); }}
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold mx-0.5 transition-all hover:opacity-80"
          style={{ background: "rgba(139,92,246,0.12)", color: "#8B5CF6", border: "1px solid rgba(139,92,246,0.25)" }}>
          <span>{s.icon}</span><span>{s.label[lang] || s.label.en}</span>
        </button>
      );
    }
    return part;
  });
}

const TYPE_CFG = {
  general:      { icon: MessageSquare, color: "#8B5CF6", label: { en: "General",   ru: "Общее",      he: "כללי"   } },
  order_update: { icon: Package,       color: "#8B5CF6", label: { en: "Orders",    ru: "Заказы",     he: "הזמנות" } },
  promotion:    { icon: Gift,          color: "#22c55e", label: { en: "Promo",     ru: "Акции",      he: "מבצעים" } },
  warning:      { icon: AlertTriangle, color: "#F77737", label: { en: "Warning",   ru: "Внимание",   he: "אזהרה"  } },
  broadcast:    { icon: Megaphone,     color: "#E1306C", label: { en: "Broadcast", ru: "Трансляция", he: "שידור"  } },
};

function relativeTime(iso, lang) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 2)  return t("DashboardV2.just_now", lang);
  if (m < 60) return L(lang, `${m}m ago`, `${m} мин`, m === 1 ? "לפני דקה"  : `לפני ${m} דקות`);
  if (h < 24) return L(lang, `${h}h ago`, `${h} ч`,   h === 1 ? "לפני שעה"  : `לפני ${h} שעות`);
  if (d < 7)  return L(lang, `${d}d ago`, `${d} дн`,  d === 1 ? "לפני יום"  : `לפני ${d} ימים`);
  return formatIsraelDate(iso);
}

const FILTERS = ["all", "general", "order_update", "promotion", "warning", "broadcast"];

export default function Messages() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("all");
  const [expanded, setExpanded] = useState(null);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/messages`, { credentials: "include" });
      if (res.ok) setMessages(await res.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    fetch(`${API}/messages/read-all`, { method: "PUT", credentials: "include" }).catch(() => {});
    fetchMessages().then(() => {
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      window.dispatchEvent(new Event("ifollowers:messages-read"));
    });
    const onLive = () => fetchMessages();
    window.addEventListener("ifollowers:live", onLive);
    return () => window.removeEventListener("ifollowers:live", onLive);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markAsRead = async (msgId) => {
    await fetch(`${API}/messages/${msgId}/read`, { method: "PUT", credentials: "include" });
    setMessages(prev => prev.map(m => m.message_id === msgId ? { ...m, is_read: true } : m));
  };

  const markAllRead = async () => {
    await fetch(`${API}/messages/read-all`, { method: "PUT", credentials: "include" });
    setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
  };

  const toggleExpand = (id, isRead) => {
    setExpanded(prev => prev === id ? null : id);
    if (!isRead) markAsRead(id);
  };

  const unread = messages.filter(m => !m.is_read).length;
  const visible = filter === "all"
    ? messages
    : messages.filter(m => (m.message_type || (m.is_broadcast ? "broadcast" : "general")) === filter);

  const filterLabel = (f) => {
    if (f === "all") return t("shop.all", lang);
    return TYPE_CFG[f]?.label[lang] || TYPE_CFG[f]?.label.en || f;
  };
  const filterCount = (f) => f === "all"
    ? messages.length
    : messages.filter(m => (m.message_type || (m.is_broadcast ? "broadcast" : "general")) === f).length;
  const usedFilters = FILTERS.filter(f => f === "all" || messages.some(m =>
    (m.message_type || (m.is_broadcast ? "broadcast" : "general")) === f
  ));

  if (loading) return (
    <div className="min-h-screen pt-14 flex items-center justify-center">
      <div className="animate-spin h-6 w-6 border-2 rounded-full" style={{ borderColor:"#8B5CF6", borderTopColor:"transparent" }} />
    </div>
  );

  return (
    <div className="min-h-screen pt-14 pb-20" data-testid="messages-page">
      <div className="max-w-xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black font-outfit" style={{ color:"var(--text-primary)" }}>
                {t("nav.messages", lang)}
              </h1>
              <p className="text-sm mt-1" style={{ color:"var(--text-secondary)" }}>
                {unread > 0
                  ? L(lang, `${unread} unread`, `${unread} непрочитанных`, `${unread} לא נקראו`)
                  : t("Messages.all_caught_up", lang)}
              </p>
            </div>
            {unread > 0 && (
              <button onClick={markAllRead}
                className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                style={{ background:"var(--surface-2)", color:"var(--text-secondary)", border:"1px solid var(--border-color)" }}
                data-testid="mark-all-read">
                <CheckCheck className="w-3.5 h-3.5" />
                {t("Messages.mark_all_read", lang)}
              </button>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        {usedFilters.length > 1 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-5">
            {usedFilters.map(f => {
              const active = filter === f;
              const cfg = TYPE_CFG[f];
              const cnt = filterCount(f);
              return (
                <button key={f} onClick={() => setFilter(f)}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold transition-all"
                  style={{
                    background: active ? (cfg ? `${cfg.color}18` : "var(--surface-3)") : "var(--surface-1)",
                    border: `1px solid ${active ? (cfg?.color || "var(--border-color)") + "40" : "var(--border-color)"}`,
                    color: active ? (cfg?.color || "var(--text-primary)") : "var(--text-secondary)",
                  }}>
                  {filterLabel(f)}
                  <span className="text-[10px] opacity-60">{cnt}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {visible.length === 0 ? (
          <div className="rounded-2xl border p-12 text-center" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
            <MailOpen className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color:"var(--text-secondary)" }} />
            <p className="font-semibold text-sm" style={{ color:"var(--text-primary)" }}>
              {t("Messages.no_messages", lang)}
            </p>
            <p className="text-xs mt-1" style={{ color:"var(--text-secondary)" }}>
              {filter !== "all"
                ? t("Messages.try_another_filter", lang)
                : t("Messages.you_ll_see_notifications_here", lang)}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)" }}>
            {visible.map((msg, idx) => {
              const type = msg.message_type || (msg.is_broadcast ? "broadcast" : "general");
              const cfg = TYPE_CFG[type] || TYPE_CFG.general;
              const Icon = cfg.icon;
              const isExpanded = expanded === msg.message_id;
              const subject = msg.subject?.[lang] || msg.subject?.en || "";
              const content  = msg.content?.[lang]  || msg.content?.en  || "";

              return (
                <div key={msg.message_id}>
                  {idx > 0 && <div style={{ height:"1px", background:"var(--border-color)" }} />}

                  <div
                    className="cursor-pointer transition-colors"
                    style={{
                      background: isExpanded ? "var(--surface-2)" : msg.is_read ? "transparent" : "var(--surface-1)",
                      borderInlineStart: !msg.is_read ? `3px solid ${cfg.color}` : "3px solid transparent",
                    }}
                    onClick={() => toggleExpand(msg.message_id, msg.is_read)}
                    data-testid={`message-${msg.message_id}`}>

                    <div className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        {/* Icon */}
                        <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center"
                          style={{ background:`${cfg.color}15`, color: cfg.color }}>
                          <Icon className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold leading-snug truncate"
                              style={{ color: msg.is_read ? "var(--text-secondary)" : "var(--text-primary)" }}>
                              {subject}
                            </span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-[11px]" dir="auto" style={{ color:"var(--text-secondary)", opacity:0.6 }}>
                                {relativeTime(msg.created_at, lang)}
                              </span>
                              {!msg.is_read && (
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
                              )}
                            </div>
                          </div>

                          {!isExpanded && (
                            <p className="text-xs mt-0.5 truncate" style={{ color:"var(--text-secondary)", opacity:0.7 }}>
                              {content.replace(/\{[\w]+\}/g, "").trim()}
                            </p>
                          )}
                        </div>

                        <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 transition-transform"
                          style={{ color:"var(--text-secondary)", opacity:0.4, transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
                      </div>

                      {/* Expanded */}
                      {isExpanded && (
                        <div className="mt-3 pt-3" style={{ borderTop:"1px solid var(--border-color)" }}>
                          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color:"var(--text-primary)" }}>
                            {renderMsgContent(content, navigate, lang)}
                          </p>
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                                style={{ background:`${cfg.color}18`, color: cfg.color }}>
                                {msg.sender_id === "system" ? "S" : "A"}
                              </div>
                              <span className="text-xs" style={{ color:"var(--text-secondary)" }}>
                                {msg.sender_id === "system"
                                  ? t("Admin.system", lang)
                                  : t("Messages.administrator", lang)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                style={{ background:`${cfg.color}12`, color: cfg.color }}>
                                {cfg.label[lang] || cfg.label.en}
                              </span>
                              <span className="text-[11px]" dir="ltr" style={{ color:"var(--text-secondary)", opacity:0.6 }}>
                                {formatIsraelDateTime(msg.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
